"use client";

import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { setTheme, initializeTheme } from "@/redux/slices/uiSlice";
import { userSettingsAPI } from "@/lib/services/api.service";
import { selectUser } from "@/redux/slices/authSlice";

/**
 * ThemeProvider - Initializes and manages theme globally
 *
 * Features:
 * - Loads theme from localStorage immediately (no blocking)
 * - Fetches user preference from backend asynchronously (non-blocking)
 * - Updates theme when user preference changes
 * - Handles system preference for "auto" mode
 */
export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const hasLoadedBackendTheme = useRef(false);

  // Initialize theme immediately from localStorage (non-blocking)
  // This runs synchronously on mount to prevent flash of wrong theme
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Apply theme from localStorage immediately (before React render)
      // This prevents flash of unstyled content
      const savedTheme = localStorage.getItem("theme") as "light" | "dark" | "auto" | null;
      const themeToApply = savedTheme || "auto";

      // Apply to DOM immediately (before React hydration)
      if (themeToApply !== "auto") {
        document.documentElement.dataset.theme = themeToApply;
      } else {
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        document.documentElement.dataset.theme = prefersDark ? "dark" : "light";
      }

      // Then update Redux state (non-blocking)
      dispatch(setTheme(themeToApply));

      // Initialize system preference listener for "auto" mode
      initializeTheme();
    }
  }, [dispatch]);

  // Load theme preference from backend when user is available (non-blocking)
  // Only load once when user first loads, not when theme changes
  useEffect(() => {
    if (!user?.id) {
      hasLoadedBackendTheme.current = false; // Reset when user logs out
      return; // Wait for user to be loaded
    }

    // Skip if we've already loaded the theme for this user
    if (hasLoadedBackendTheme.current) {
      return;
    }

    // Fetch user preferences asynchronously (don't block rendering)
    const loadThemePreference = async () => {
      try {
        // Ensure authenticated users default to auto while preferences load.
        dispatch(setTheme("auto"));

        const response = await userSettingsAPI.getUserPreferences();
        const backendTheme = response?.preferences?.theme ?? "auto";
        // Always apply backend theme when user first loads (fallback to auto)
        dispatch(setTheme(backendTheme));
        // Mark as loaded after successful fetch
        hasLoadedBackendTheme.current = true;
      } catch (error) {
        // Silently fail - theme from localStorage will be used
        // Don't log errors in production to avoid noise
        if (import.meta.env.DEV === true) {
          console.warn("[ThemeProvider] Failed to load theme preference:", error);
        }
        // Mark as loaded even on error to prevent retry loops
        hasLoadedBackendTheme.current = true;
      }
    };

    // Load in background without blocking
    loadThemePreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, dispatch]);

  return <>{children}</>;
}
